import { useState, useEffect } from 'react';
import { getDestinationPhoto } from './api/atlast';

// Photo lookups are cached on the phone for a week, and identical lookups
// made at the same moment share ONE request. A Dashboard with 16 pins used
// to fire 16 /photos calls (some for the same city) on every single load,
// which queues badly on a phone connection.
const TTL_MS = 7 * 24 * 60 * 60 * 1000;
const mem = new Map();        // key -> photo url (or null)
const inflight = new Map();   // key -> promise
const keyOf = (d) => 'fa_photo_v1:' + String(d || '').trim().toLowerCase();

function readStored(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return undefined;
    const { u, t } = JSON.parse(raw);
    if (Date.now() - t > TTL_MS) return undefined;
    return u || null;
  } catch { return undefined; }
}
function writeStored(key, url) {
  // Only remember real photos; a "no photo yet" answer is re-checked next time.
  try { if (url) localStorage.setItem(key, JSON.stringify({ u: url, t: Date.now() })); } catch { /* storage full or blocked: fine */ }
}

function lookup(destination) {
  const key = keyOf(destination);
  if (mem.has(key)) return Promise.resolve(mem.get(key));
  const stored = readStored(key);
  if (stored !== undefined) { mem.set(key, stored); return Promise.resolve(stored); }
  if (inflight.has(key)) return inflight.get(key);
  const p = getDestinationPhoto(destination)
    .then((res) => {
      const url = (res && res.photo_url) || null;
      if (url) { mem.set(key, url); writeStored(key, url); }
      return url;
    })
    .catch(() => null)
    .finally(() => inflight.delete(key));
  inflight.set(key, p);
  return p;
}

// Pexels photo URLs carry their size in the query string. A small card banner
// does not need the 1200px version, so ask for a smaller one.
export function sizedPhoto(url, width = 640, height = 200) {
  if (!url || !/images\.pexels\.com/.test(url)) return url;
  return url.replace(/([?&])h=\d+/, `$1h=${height}`).replace(/([?&])w=\d+/, `$1w=${width}`);
}

export function useDestinationPhoto(destination) {
  const [photoUrl, setPhotoUrl] = useState(() => {
    if (!destination) return null;
    const key = keyOf(destination);
    if (mem.has(key)) return mem.get(key);
    const stored = readStored(key);
    return stored === undefined ? null : stored;
  });

  useEffect(() => {
    let cancelled = false;
    if (!destination) {
      setPhotoUrl(null);
      return undefined;
    }
    lookup(destination).then((url) => { if (!cancelled) setPhotoUrl(url); });
    return () => { cancelled = true; };
  }, [destination]);

  return photoUrl;
}
